import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  KeyValue,
  Progress,
  SectionHeader,
  Typography,
  WordCountArea,
} from 'web-library';
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import ControlledAutocomplete from 'web-library/components/form-items/ControlledAutocomplete';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useMediaQuery } from '@mui/material';
import { ConfigurationType } from 'web-library/models/DropDown';
import TaskFileBadge from 'web-library/components/TaskFileBadge';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';
import { AxiosError } from 'axios';
import { useGetProfileAmendmentRequestDetails } from '../../../../api-refactored/profile-amendment-requests/useGetProfileAmendmentRequestDetails';
import { useGetProfileAmendmentRequestStatuses } from '../../../../api-refactored/profile-amendment-requests/useGetProfileAmendmentRequestStatuses';
import { profileAmendmentRequestSchema } from '../../schema/profileAmendmentRequestSchema';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { usePutUpdateProfileAmendmentRequestStatus } from '../../../../api-refactored/profile-amendment-requests/usePutUpdateProfileAmendmentRequestStatus';
import { ProfileAmendmentRequestStatus } from '../../../../api-refactored/profile-amendment-requests/usePutCancelProfileAmendmentRequest';
import {
  displayProfileAmendmentRequestPillStatus,
  useCheckAdminAccess,
} from './ProfileAmendmentRequestsDataTable';
import ProfileAmendmentRequestTransactionLog from './ProfileAmendmentRequestTransactionLog';
import { GenericErrorResponse } from '../../../../api-refactored/typings';

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(4),
  },
  divider: {
    marginTop: theme.spacing(2),
  },
  status: {
    maxWidth: '600px',
    marginBottom: theme.spacing(4),
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(7),
  },
  backToApplication: {
    height: 'fit-content',
  },
  updateStatus: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
      marginTop: theme.spacing(3),
    },
  },
}));

const ReviewProfileAmendmentApplicationDetails = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fileUuid, setFileUuid] = useState<string>('');
  const [openTransactionLogModal, setOpenTransactionLogModal] = useState<boolean>(false);

  useCheckAdminAccess();

  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(profileAmendmentRequestSchema),
  });

  const { control, getValues, handleSubmit, formState: { errors } } = formHook;

  const {
    data: profileAmendmentRequestDetails,
    isLoading: isLoadingProfileAmendmentRequestDetails,
  } = useGetProfileAmendmentRequestDetails({ uuid });

  const { data: profileAmendmentRequestStatuses } = useGetProfileAmendmentRequestStatuses();

  useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && fileUuid !== '',
    refetchInterval: undefined,
    onSuccess: (res) => {
      if (res.url) {
        const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        setFileUuid('');
      }
    },
    onError: (error: AxiosError<GenericErrorResponse>) =>
      setErrorMessage(error?.response?.data?.message!),
  });

  const handleBackToProfileAmendmentRequestListing = () => history.push('../');

  const {
    mutate: mutatePutUpdateProfileAmendmentRequestStatus,
    status: updateProfileAmendmentRequestStatus,
  } = usePutUpdateProfileAmendmentRequestStatus({
    onSuccess: handleBackToProfileAmendmentRequestListing,
    onError: (error) => setErrorMessage(error?.response?.data?.message!),
    uuid,
  });

  const handleUpdateStatus = () =>
    mutatePutUpdateProfileAmendmentRequestStatus({
      status: getValues('status').code,
      remarks: getValues('remarks'),
    });

  const isEditableProfileAmendmentDetails =
    profileAmendmentRequestDetails?.status === ProfileAmendmentRequestStatus.Incomplete ||
    profileAmendmentRequestDetails?.status === ProfileAmendmentRequestStatus.Pending;

  if (isLoadingProfileAmendmentRequestDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <ContentTemplate>
      <form onSubmit={handleSubmit(handleUpdateStatus)}>
        <SectionHeader title={t('admin->>ProfileAmendmentRequests.ViewDetails')} />
        <Typography className={classes.title} variant="h3">
          {t('admin->>ProfileAmendmentRequests.ChangeSummary')}
        </Typography>
        {profileAmendmentRequestDetails?.content &&
          profileAmendmentRequestDetails?.content.map((item) => {
            const oldAndNewValue = [];
            oldAndNewValue.push(
              t('admin->>ProfileAmendmentRequests.OldValue', { oldValue: item.before })
            );
            oldAndNewValue.push(
              t('admin->>ProfileAmendmentRequests.NewValue', { newValue: item.after })
            );
            return (
              <KeyValue
                key={item.baseFieldName}
                keyText={item.baseFieldName}
                valueText={oldAndNewValue}
              />
            );
          })}
        <Divider className={classes.divider} />
        <Typography className={classes.title} variant="h3">
          {t('admin->>ProfileAmendmentRequests.StatusUpdate')}
        </Typography>

        <KeyValue
          keyText={t('admin->>ProfileAmendmentRequests.Requestor')}
          valueText={profileAmendmentRequestDetails?.requestorName}
        />
        <KeyValue
          keyText={t('admin->>ProfileAmendmentRequests.Action')}
          valueText={t('admin->>ProfileAmendmentRequests.Edit')}
        />
        <KeyValue
          keyText={t('admin->>ProfileAmendmentRequests.Remarks')}
          valueText={profileAmendmentRequestDetails?.requestorRemarks || '-'}
        />
        <KeyValue
          keyText={t('admin->>TaskDetails.AttachedFiles')}
          noPadding
          valueText={
            profileAmendmentRequestDetails && profileAmendmentRequestDetails.files.length > 0 ? (
              <>
                {profileAmendmentRequestDetails?.files.map((f) => (
                  <TaskFileBadge key={f.fileUuid} file={f} setFileUuid={setFileUuid} />
                ))}
              </>
            ) : (
              '-'
            )
          }
        />
        {isEditableProfileAmendmentDetails ? (
          <>
            <ControlledAutocomplete<ConfigurationType, false, true, undefined>
              className={classes.status}
              name="status"
              control={control}
              label={t('admin->>ProfileAmendmentRequests.Status')}
              options={profileAmendmentRequestStatuses as ConfigurationType[]}
              getOptionLabel={({ description }) => description}
              renderOption={(_props, option, _state, _) => option.description}
              placeholder={t('admin->>ProfileAmendmentRequests.SelectStatus')}
              error={!!errors.status}
              disableClearable
              readOnly
              helperText={errors.status?.message?.toString()}
              isRequired
            />
            <WordCountArea
                form={formHook}
                name="remarks"
                className={classes.status}
                error={!!errors.remarks}
                label={t('admin->>ProfileAmendmentRequests.RemarksBySEAB')}
                placeholder={t('admin->>ProfileAmendmentRequests.PleaseEnterRemarks')}
                maxLength={200}
                minRows={3}
                // CPEPOKPNEH-3391
                onChange={(e) => {
                    const trimmedValue = e.target.value.slice(0, 200); // enforce maxLength
                    formHook.setValue('remarks', trimmedValue);
                }}
            />
          </>
        ) : (
          <KeyValue
            keyText={t('admin->>ProfileAmendmentRequests.Status')}
            valueText={displayProfileAmendmentRequestPillStatus(
              profileAmendmentRequestDetails?.status as string
            )}
          />
        )}
        <TransactionAuditLogUpdatedDetails
          updatedAt={profileAmendmentRequestDetails?.updatedAt as string}
          updatedBy={profileAmendmentRequestDetails?.updatedBy as string}
          handleOpenLogModal={() => setOpenTransactionLogModal(true)}
          label={t('admin->>UserAccountManagement.ViewTransactionLog')}
        />
        {openTransactionLogModal && (
          <ProfileAmendmentRequestTransactionLog
            modalState={openTransactionLogModal}
            setOpenModalState={setOpenTransactionLogModal}
          />
        )}
        {errorMessage && (
          <Typography variant="body2" className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}
        <Box className={classes.responsiveFlexContainer}>
          <Button
            className={classes.backToApplication}
            id="button-profile-amendment-requests-back-to-application"
            variant="outlined"
            fullWidth={isMobile}
            onClick={handleBackToProfileAmendmentRequestListing}
          >
            {isEditableProfileAmendmentDetails
              ? t('admin->>ProfileAmendmentRequests.Cancel')
              : t('admin->>ProfileAmendmentRequests.BackToProfileAmendmentApplications')}
          </Button>
          {isEditableProfileAmendmentDetails && (
            <ApiButton
              className={classes.updateStatus}
              id="button-profile-amendment-requests-update-status"
              status={updateProfileAmendmentRequestStatus}
              type="submit"
              fullWidth={isMobile}
            >
              {t('admin->>ProfileAmendmentRequests.UpdateStatus')}
            </ApiButton>
          )}
        </Box>
      </form>
    </ContentTemplate>
  );
};

export default ReviewProfileAmendmentApplicationDetails;
